import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, CarFront, Eye, EyeOff } from "lucide-react";
import { http } from "@/lib/auth";
import { photoUrl } from "@/lib/vehicles";
import { VehicleFormDialog } from "./VehicleFormDialog";

const STATUS = { available: "bg-emerald-50 text-emerald-700 border-emerald-200", reserved: "bg-amber-50 text-amber-700 border-amber-200", sold: "bg-slate-100 text-slate-600 border-slate-200" };

export default function AdminVehicles() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => http.get("/admin/vehicles").then((r) => setItems(r.data)).catch(() => toast.error("Could not load vehicles"));
  useEffect(() => { load(); }, []);

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.make} ${v.model}?`)) return;
    try {
      await http.delete(`/admin/vehicles/${v.id}`);
      setItems((l) => l.filter((x) => x.id !== v.id));
      toast.success("Vehicle deleted");
    } catch {
      toast.error("Could not delete vehicle");
    }
  };
  const removeSamples = async () => {
    if (!window.confirm("Remove all sample vehicles?")) return;
    try {
      const { data } = await http.delete("/admin/vehicles/samples");
      setItems((l) => l.filter((x) => !x.is_sample));
      toast.success(`${data.deleted} sample vehicle(s) removed`);
    } catch {
      toast.error("Could not remove samples");
    }
  };
  const onSaved = (v) => {
    setItems((l) => (l.some((x) => x.id === v.id) ? l.map((x) => (x.id === v.id ? v : x)) : [v, ...l]));
    setEditing(null);
  };
  const hasSamples = (items || []).some((v) => v.is_sample);

  return (
    <div data-testid="admin-vehicles">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-3xl font-bold text-[#0A2540] sm:text-4xl">Vehicles</h1>
        <div className="flex gap-2">
          {hasSamples && <button data-testid="admin-remove-samples" onClick={removeSamples} className="rounded-full border border-red-200 px-4 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50">Remove sample vehicles</button>}
          <button data-testid="admin-add-vehicle" onClick={() => setEditing({})} className="inline-flex items-center gap-2 rounded-full bg-[#2F7BFF] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#1F63E0]"><Plus className="h-4 w-4" />Add vehicle</button>
        </div>
      </div>
      {hasSamples && <p className="mt-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">Vehicles marked “Sample” are placeholders to show the page. Replace them with your real stock or remove them.</p>}
      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {items === null ? (
          <div className="grid h-48 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div data-testid="admin-vehicles-empty" className="flex h-48 flex-col items-center justify-center gap-2 text-slate-500"><CarFront className="h-8 w-8" />No vehicles yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="admin-vehicles-table" className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-3">Vehicle</th><th className="px-5 py-3">Year / km</th><th className="px-5 py-3">Location</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Visible</th><th className="px-5 py-3" /></tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.id} data-testid={`admin-vehicle-row-${v.id}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-100">{v.photos[0] && <img src={photoUrl(v.photos[0])} alt="" className="h-full w-full object-cover" />}</div>
                        <div><div className="font-bold text-[#0A2540]">{v.make} {v.model} {v.is_sample && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">Sample</span>}</div><div className="text-slate-500">{v.variant}</div></div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-slate-700">{v.year} · {v.mileage_km.toLocaleString("en-GB")} km</td>
                    <td className="whitespace-nowrap px-5 py-3 text-slate-700">{v.location || "—"}</td>
                    <td className="px-5 py-3"><span className={`rounded-full border px-3 py-1 text-xs font-bold capitalize ${STATUS[v.status]}`}>{v.status}</span></td>
                    <td className="px-5 py-3 text-slate-500">{v.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</td>
                    <td className="whitespace-nowrap px-5 py-3 text-right">
                      <button data-testid={`admin-edit-vehicle-${v.id}`} onClick={() => setEditing(v)} aria-label="Edit" className="rounded-full p-2 text-[#0A2540] hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`admin-delete-vehicle-${v.id}`} onClick={() => remove(v)} aria-label="Delete" className="rounded-full p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing && <VehicleFormDialog vehicle={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}
